
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
 Input , Label } from "@roberttamas/ui";
import { useState, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Loader2 } from "@/components/icons";
import { cn } from "@/lib/utils";

interface Props {
  trigger: ReactNode;
  title: string;
  description: ReactNode;
  confirmWord: string;
  confirmLabel?: string;
  busyLabel?: string;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDestructiveDialog({
  trigger,
  title,
  description,
  confirmWord,
  confirmLabel,
  busyLabel,
  onConfirm,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const matches = typed === confirmWord;

  const reset = () => {
    setTyped("");
    setBusy(false);
  };

  const handleConfirm = async () => {
    if (!matches || busy) return;
    setBusy(true);
    try {
      await onConfirm();
      // Only close on success. On failure we let the caller toast and we
      // keep the dialog open so the operator can retry without re-typing.
      setOpen(false);
      reset();
    } catch {
      // Error handling lives in `onConfirm`'s caller (mutation onError).
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return; // Don't allow dismissing mid-operation.
        setOpen(next);
        if (!next) reset();
      }}
    >
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>{description}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="confirm-input" className="text-sm">
            <Trans
              i18nKey="confirmDialog.typeToConfirm"
              values={{ word: confirmWord }}
              components={{ w: <span className="font-mono font-semibold" /> }}
            />
          </Label>
          <Input
            id="confirm-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className={cn(
              "bg-destructive text-destructive-foreground hover:bg-destructive/90",
              (!matches || busy) && "cursor-not-allowed opacity-50",
            )}
            disabled={!matches || busy}
            onClick={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
          >
            {busy ? (
              <>
                <Loader2 className="animate-spin" />
                {busyLabel ?? t("common.working")}
              </>
            ) : (
              confirmLabel ?? t("common.delete")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
